import type { RecordSubscription, UnsubscribeFunc } from 'pocketbase';
import { pb, protectedFileUrl } from '../lib/pb';
import type { BaseLayerRecord, FeatureRecord, MapRecord, UnityObjectRecord, UserRecord } from '../lib/types';
import { errorMessage } from '../lib/errors';
import { getState, setState, toast } from './store';

// Loading + realtime. Realtime events are LOST while disconnected, so every (re)connect refetches.

let unsubs: UnsubscribeFunc[] = [];
let connectedOnce = false;

export async function loadMaps(): Promise<MapRecord[]> {
  const maps = await pb
    .collection('maps')
    .getFullList<MapRecord>({ sort: 'sortOrder,name', filter: 'archived=false' });
  setState({ maps });
  return maps;
}

async function fetchMapData(mapId: string) {
  const filter = pb.filter('map = {:m}', { m: mapId });
  const [features, baseLayers, unityObjects, users, map] = await Promise.all([
    pb.collection('features').getFullList<FeatureRecord>({ filter, batch: 1000 }),
    pb.collection('base_layers').getFullList<BaseLayerRecord>({ filter, sort: 'sortOrder,created' }),
    pb.collection('unity_objects').getFullList<UnityObjectRecord>({ filter, batch: 1000 }),
    pb.collection('users').getFullList<UserRecord>({ sort: 'name' }),
    pb.collection('maps').getOne<MapRecord>(mapId),
  ]);
  const s = getState();
  setState({
    features: Object.fromEntries(features.map((f) => [f.id, f])),
    baseLayers,
    unityObjects: Object.fromEntries(unityObjects.map((u) => [u.unityKey, u])),
    users: Object.fromEntries(users.map((u) => [u.id, u])),
    maps: s.maps.map((m) => (m.id === map.id ? map : m)),
    layerView: {
      ...Object.fromEntries(
        baseLayers.map((l) => [l.id, { visible: l.visibleByDefault, opacity: l.opacity || 1 }]),
      ),
      ...s.layerView,
    },
  });
  void loadHeightmap(map);
}

async function loadHeightmap(map: MapRecord) {
  const meta = map.heightmapMeta;
  if (!map.heightmap || !meta) {
    setState({ heightmap: null });
    return;
  }
  try {
    const url = await protectedFileUrl(map, map.heightmap);
    const buf = await (await fetch(url)).arrayBuffer();
    const data = new Float32Array(buf);
    if (data.length !== meta.width * meta.height) throw new Error('heightmap size mismatch');
    setState({ heightmap: { data, ...meta } });
  } catch (err) {
    console.warn('heightmap', err);
    setState({ heightmap: null });
  }
}

function upsertById<T extends { id: string }>(list: T[], rec: T, action: string): T[] {
  if (action === 'delete') return list.filter((x) => x.id !== rec.id);
  const i = list.findIndex((x) => x.id === rec.id);
  if (i < 0) return [...list, rec];
  const copy = list.slice();
  copy[i] = rec;
  return copy;
}

async function subscribeMap(mapId: string) {
  for (const u of unsubs) await u().catch(() => undefined);
  unsubs = [];
  const filter = pb.filter('map = {:m}', { m: mapId });

  unsubs.push(
    await pb.collection('features').subscribe<FeatureRecord>(
      '*',
      (e: RecordSubscription<FeatureRecord>) => {
        const features = { ...getState().features };
        if (e.action === 'delete') delete features[e.record.id];
        else features[e.record.id] = e.record;
        const sel = getState().selectedId;
        setState({ features, selectedId: e.action === 'delete' && sel === e.record.id ? null : sel });
      },
      { filter },
    ),
    await pb.collection('base_layers').subscribe<BaseLayerRecord>(
      '*',
      (e) => {
        const s = getState();
        setState({
          baseLayers: upsertById(s.baseLayers, e.record, e.action).sort((a, b) => a.sortOrder - b.sortOrder),
          layerView:
            e.action === 'create' && !s.layerView[e.record.id]
              ? {
                  ...s.layerView,
                  [e.record.id]: { visible: e.record.visibleByDefault, opacity: e.record.opacity || 1 },
                }
              : s.layerView,
        });
      },
      { filter },
    ),
    await pb.collection('unity_objects').subscribe<UnityObjectRecord>(
      '*',
      (e) => {
        const unityObjects = { ...getState().unityObjects };
        if (e.action === 'delete') delete unityObjects[e.record.unityKey];
        else unityObjects[e.record.unityKey] = e.record;
        setState({ unityObjects });
      },
      { filter },
    ),
    await pb.collection('maps').subscribe<MapRecord>(mapId, (e) => {
      const s = getState();
      setState({ maps: s.maps.map((m) => (m.id === e.record.id ? e.record : m)) });
      const old = s.maps.find((m) => m.id === e.record.id);
      if (old?.heightmap !== e.record.heightmap) void loadHeightmap(e.record);
    }),
    await pb.collection('users').subscribe<UserRecord>('*', (e) => {
      const users = { ...getState().users };
      if (e.action === 'delete') delete users[e.record.id];
      else users[e.record.id] = e.record;
      setState({ users });
    }),
  );
}

export async function openMap(mapId: string) {
  setState({ mapId, loading: true, selectedId: null, editingShapeId: null, tool: { kind: 'none' } });
  try {
    await fetchMapData(mapId);
    await subscribeMap(mapId);
  } catch (err) {
    toast(errorMessage(err), 'error');
  } finally {
    setState({ loading: false });
  }
}

/** Wire the connection indicator and refetch-on-reconnect once per session. */
export function initRealtime() {
  pb.realtime.onDisconnect = () => setState({ realtime: getState().mapId ? 'offline' : 'connecting' });
  void pb.realtime.subscribe('PB_CONNECT', () => {
    setState({ realtime: 'connected' });
    const mapId = getState().mapId;
    // First connect happens during openMap(); later ones are reconnects that missed events.
    if (connectedOnce && mapId) void fetchMapData(mapId).catch((err) => toast(errorMessage(err), 'error'));
    connectedOnce = true;
  });
}

export async function disposeRealtime() {
  for (const u of unsubs) await u().catch(() => undefined);
  unsubs = [];
  await pb.realtime.unsubscribe().catch(() => undefined);
  connectedOnce = false;
}

/** Height (Unity Y) at a world position, bilinear, or null outside / without a heightmap. */
export function sampleHeight(x: number, z: number): number | null {
  const h = getState().heightmap;
  if (!h) return null;
  const fx = (x - h.minX) / h.step;
  const fz = (h.maxZ - z) / h.step; // row 0 = north edge
  if (fx < 0 || fz < 0 || fx > h.width - 1 || fz > h.height - 1) return null;
  const x0 = Math.floor(fx),
    z0 = Math.floor(fz);
  const x1 = Math.min(x0 + 1, h.width - 1),
    z1 = Math.min(z0 + 1, h.height - 1);
  const tx = fx - x0,
    tz = fz - z0;
  const at = (c: number, r: number) => h.data[r * h.width + c];
  const a = at(x0, z0) * (1 - tx) + at(x1, z0) * tx;
  const b = at(x0, z1) * (1 - tx) + at(x1, z1) * tx;
  return a * (1 - tz) + b * tz;
}
